import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Compass, Wrench, Globe, BookOpen, Footprints, Users, CheckCircle2, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker, Tag } from "@/components/Reveal";
import { Practitioners } from "@/components/Practitioners";
import { Chip, StepProgress, StepNav } from "@/components/StepBits";
import { Field, TextArea } from "@/components/FormBits";
import { ClosingMessage, PolicyLinks, CrisisNote } from "@/components/SharedSections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_MANAGED = ["Self-managed", "Plan-managed", "NDIA-managed", "Combination", "I'm not sure"];
const SUPPORT_TYPES = ["Mentoring", "Skill Development", "Community-Based Support", "Social & Community Participation", "Narrative Practice", "Parent/Carer Capacity Building", "Cultural Identity & Connection", "Movement incorporated into support", "Children & Young People's Support", "Combination", "I'm not sure — help me choose"];
const GOALS = ["Confidence", "Independence", "Communication", "Relationships", "Social skills", "Community participation", "Emotional regulation", "Everyday skills", "Identity and belonging", "Cultural connection", "Education", "Life transitions", "Building routines", "Decision making", "Trying new things", "Building on existing strengths", "Other"];
const PRAC_PREFS = ["No preference", "Aboriginal practitioner", "Female practitioner", "Male practitioner", "Experience with children/young people", "Narrative Therapy experience", "Trauma-informed practice", "Cultural identity and connection", "Mentoring experience", "Family/relational work", "Movement/fitness", "Other"];
const REFERRER_TYPES = ["Participant", "Parent/guardian", "Nominee/representative", "Support Coordinator", "Recovery Coach", "Plan Manager", "NDIS/LAC representative", "School/education provider", "Allied health/health professional", "Community organisation", "Other service"];

const EMPTY = {
  participant_name: "", preferred_name: "", dob: "", pronouns: "", phone: "", email: "", preferred_contact: "",
  guardian_name: "", guardian_relationship: "", guardian_contact: "",
  has_plan: "", plan_managed: "", plan_manager_org: "", plan_manager_name: "", plan_manager_email: "",
  plan_manager_phone: "", funding_relevant: "", plan_info: "",
  support_types: [], goals: [], own_words: "", practitioner_preferences: [],
  referrer_type: "", referrer_name: "", organisation: "", position: "", referrer_email: "", referrer_phone: "",
  consent_known: "", confirm: false,
};

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const NdisRequestForm = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setEv = (k) => (e) => set(k, e.target.type === "checkbox" ? e.target.checked : e.target.value);

  const isProfessional = form.referrer_type && !["Participant", "Parent/guardian", "Nominee/representative"].includes(form.referrer_type);

  const next = async () => {
    if (step === 1 && (!form.participant_name.trim() || !form.email.trim())) return toast.error("Please add the participant's name and an email.");
    if (step === 2 && form.has_plan !== "Yes") return toast.error("Please confirm the active NDIS plan to continue.");
    if (step === 2 && !form.plan_managed) return toast.error("Let us know how the plan is managed — 'I'm not sure' is fine.");
    if (step === 7) {
      if (!form.confirm) return toast.error("Please tick the confirmation box first.");
      setBusy(true);
      try {
        await axios.post(`${API}/ndis-requests`, form);
        setDone(true);
        window.scrollTo(0, 0);
      } catch {
        toast.error("Something went wrong sending that. Please try again.");
      } finally {
        setBusy(false);
      }
      return;
    }
    setStep(step + 1);
  };

  if (done) {
    return (
      <div data-testid="ndis-success" className="rounded-[48px] bg-creamdark border border-ochre/25 p-12 sm:p-16 text-center">
        <CheckCircle2 className="h-12 w-12 text-terracotta mx-auto mb-6" />
        <h3 className="font-display font-light text-4xl text-charcoal">Your request has been received.</h3>
        <p className="mt-5 text-earth/85 leading-relaxed max-w-xl mx-auto">One of our team will review:</p>
        <div className="mt-5 flex flex-wrap justify-center gap-2.5 max-w-xl mx-auto">
          {["What you want to work towards", "The support you're requesting", "How it relates to your NDIS goals", "Available funding", "Plan-management arrangements", "Practitioner availability", "Service suitability"].map((t) => (
            <span key={t} className="rounded-full bg-cream border border-ochre/40 px-4 py-2 text-xs font-semibold text-earth">{t}</span>
          ))}
        </div>
        <p className="mt-6 text-earth/85">We'll contact you to yarn about the next steps.</p>
      </div>
    );
  }

  return (
    <div data-testid="ndis-request-form" className="rounded-[48px] bg-creamdark border border-ochre/25 p-8 sm:p-14">
      <StepProgress step={step} total={7} />

      {step === 1 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-6">About the participant</h3>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Participant name" testid="nd-name-input" value={form.participant_name} onChange={setEv("participant_name")} required />
            <Field label="Preferred name" testid="nd-preferred-input" optional value={form.preferred_name} onChange={setEv("preferred_name")} />
            <Field label="Date of birth" testid="nd-dob-input" type="date" optional value={form.dob} onChange={setEv("dob")} />
            <Field label="Pronouns" testid="nd-pronouns-input" optional value={form.pronouns} onChange={setEv("pronouns")} />
            <Field label="Phone" testid="nd-phone-input" optional value={form.phone} onChange={setEv("phone")} />
            <Field label="Email" testid="nd-email-input" type="email" value={form.email} onChange={setEv("email")} required />
            <Field label="Preferred contact method" testid="nd-contact-method-input" optional value={form.preferred_contact} onChange={setEv("preferred_contact")} />
          </div>
          <p className="mt-8 text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-4">If the participant is a child or has a nominee</p>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Parent/guardian/nominee name" testid="nd-guardian-name-input" optional value={form.guardian_name} onChange={setEv("guardian_name")} />
            <Field label="Relationship to participant" testid="nd-guardian-rel-input" optional value={form.guardian_relationship} onChange={setEv("guardian_relationship")} />
            <Field label="Their contact details" testid="nd-guardian-contact-input" optional value={form.guardian_contact} onChange={setEv("guardian_contact")} />
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-6">The NDIS plan</h3>
          <p className="text-sm font-semibold text-earth mb-4">Do you currently have an active NDIS plan?</p>
          <div className="flex gap-3 mb-8">
            {["Yes", "No"].map((t) => (
              <Chip key={t} testid={`nd-plan-${t.toLowerCase()}`} selected={form.has_plan === t} onClick={() => set("has_plan", form.has_plan === t ? "" : t)}>{t}</Chip>
            ))}
          </div>
          {form.has_plan === "No" && (
            <div className="rounded-3xl bg-cream border border-ochre/35 p-8" data-testid="nd-no-plan">
              <p className="font-display text-2xl text-charcoal">This pathway is for active NDIS participants.</p>
              <p className="mt-2 text-earth/85">You can still access Ngawilyi through:</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link data-testid="nd-goto-private" to="/private-supports" className="rounded-full bg-terracotta px-6 py-3 text-xs font-bold uppercase tracking-widest text-cream">Private Supports</Link>
                <Link data-testid="nd-goto-service" to="/referrals" className="rounded-full border-2 border-charcoal px-6 py-3 text-xs font-bold uppercase tracking-widest text-charcoal">Service & Program Supports</Link>
              </div>
            </div>
          )}
          {form.has_plan === "Yes" && (
            <>
              <p className="text-sm font-semibold text-earth mb-4">How is the plan managed?</p>
              <div className="flex flex-wrap gap-3 mb-6">
                {PLAN_MANAGED.map((t) => (
                  <Chip key={t} testid={`nd-managed-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.plan_managed === t} onClick={() => set("plan_managed", form.plan_managed === t ? "" : t)}>{t}</Chip>
                ))}
              </div>
              {form.plan_managed === "NDIA-managed" && (
                <p className="mb-6 rounded-3xl bg-ochre/15 border border-ochre/40 px-6 py-4 text-sm text-earth" data-testid="nd-ndia-note">
                  Ngawilyi currently operates as an unregistered provider, so NDIA-managed enquiries are reviewed
                  manually — submit your request and we'll yarn about what's possible.
                </p>
              )}
              {form.plan_managed === "Plan-managed" && (
                <div className="mb-6 grid sm:grid-cols-2 gap-6 rounded-3xl bg-cream border border-ochre/30 p-6">
                  <Field label="Plan manager organisation" testid="nd-pm-org-input" optional value={form.plan_manager_org} onChange={setEv("plan_manager_org")} />
                  <Field label="Contact name" testid="nd-pm-name-input" optional value={form.plan_manager_name} onChange={setEv("plan_manager_name")} />
                  <Field label="Email" testid="nd-pm-email-input" type="email" optional value={form.plan_manager_email} onChange={setEv("plan_manager_email")} />
                  <Field label="Phone" testid="nd-pm-phone-input" optional value={form.plan_manager_phone} onChange={setEv("plan_manager_phone")} />
                </div>
              )}
              <p className="text-sm font-semibold text-earth mb-4">Do you believe you currently have funding relevant to this support?</p>
              <div className="flex gap-3 mb-6">
                {["Yes", "No", "Unsure"].map((t) => (
                  <Chip key={t} testid={`nd-funding-${t.toLowerCase()}`} selected={form.funding_relevant === t} onClick={() => set("funding_relevant", form.funding_relevant === t ? "" : t)}>{t}</Chip>
                ))}
              </div>
              <TextArea label="Relevant NDIS plan information" testid="nd-plan-info-textarea" optional rows={3} value={form.plan_info} onChange={setEv("plan_info")} placeholder="Only what relates to the support you're seeking…" />
              <p className="mt-3 text-sm text-earth/70">You don't need to share your entire NDIS plan to make an initial request — only what's relevant to this support.</p>
            </>
          )}
        </div>
      )}

      {step === 3 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-6">What type of support are you interested in?</h3>
          <div className="flex flex-wrap gap-3">
            {SUPPORT_TYPES.map((t) => (
              <Chip key={t} testid={`nd-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.support_types.includes(t)} onClick={() => set("support_types", toggle(form.support_types, t))}>{t}</Chip>
            ))}
          </div>
        </div>
      )}

      {step === 4 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-6">What would you like support to make more possible?</h3>
          <div className="flex flex-wrap gap-3 mb-8">
            {GOALS.map((t) => (
              <Chip key={t} testid={`nd-goal-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.goals.includes(t)} onClick={() => set("goals", toggle(form.goals, t))}>{t}</Chip>
            ))}
          </div>
          <TextArea label="In your own words — what would you like support to make more possible?" testid="nd-own-words-textarea" optional value={form.own_words} onChange={setEv("own_words")} />
          <p className="mt-3 text-sm text-earth/70">No need for detailed trauma or medical history here.</p>
        </div>
      )}

      {step === 5 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-6">Who would you feel comfortable working alongside?</h3>
          <div className="flex flex-wrap gap-3">
            {PRAC_PREFS.map((t) => (
              <Chip key={t} testid={`nd-pref-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.practitioner_preferences.includes(t)} onClick={() => set("practitioner_preferences", toggle(form.practitioner_preferences, t))}>{t}</Chip>
            ))}
          </div>
          <p className="mt-5 text-sm text-earth/70">Preferences are subject to practitioner availability.</p>
        </div>
      )}

      {step === 6 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-6">Who is making this request?</h3>
          <div className="flex flex-wrap gap-3 mb-6">
            {REFERRER_TYPES.map((t) => (
              <Chip key={t} testid={`nd-referrer-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.referrer_type === t} onClick={() => set("referrer_type", form.referrer_type === t ? "" : t)}>{t}</Chip>
            ))}
          </div>
          {isProfessional && (
            <div className="grid sm:grid-cols-2 gap-6 rounded-3xl bg-cream border border-ochre/30 p-6 mb-6">
              <Field label="Referrer name" testid="nd-ref-name-input" optional value={form.referrer_name} onChange={setEv("referrer_name")} />
              <Field label="Organisation" testid="nd-ref-org-input" optional value={form.organisation} onChange={setEv("organisation")} />
              <Field label="Position" testid="nd-ref-position-input" optional value={form.position} onChange={setEv("position")} />
              <Field label="Email" testid="nd-ref-email-input" type="email" optional value={form.referrer_email} onChange={setEv("referrer_email")} />
              <Field label="Phone" testid="nd-ref-phone-input" optional value={form.referrer_phone} onChange={setEv("referrer_phone")} />
            </div>
          )}
          <p className="text-sm font-semibold text-earth mb-4">Does the participant or their authorised representative know about and consent to this referral?</p>
          <div className="flex gap-3">
            {["Yes", "No"].map((t) => (
              <Chip key={t} testid={`nd-consent-${t.toLowerCase()}`} selected={form.consent_known === t} onClick={() => set("consent_known", form.consent_known === t ? "" : t)}>{t}</Chip>
            ))}
          </div>
          {form.consent_known === "No" && (
            <p className="mt-4 rounded-3xl bg-ochre/15 border border-ochre/40 px-6 py-4 text-sm text-earth" data-testid="nd-consent-note">
              That's okay for an initial enquiry — appropriate participant or representative consent will need to
              be obtained before services commence or further participant information is collected.
            </p>
          )}
        </div>
      )}

      {step === 7 && (
        <div>
          <h3 className="font-display text-3xl text-charcoal mb-6">Almost done</h3>
          <label className="flex items-start gap-4 rounded-3xl bg-cream border border-ochre/35 px-6 py-5 cursor-pointer" data-testid="nd-confirm-wrap">
            <input data-testid="nd-confirm-checkbox" type="checkbox" checked={form.confirm} onChange={setEv("confirm")} className="mt-1 h-5 w-5 accent-[#C05746]" />
            <span className="text-sm leading-relaxed text-earth">
              I understand that submitting this request does not guarantee service availability, or that every
              requested Ngawilyi support can be purchased using NDIS funding. Ngawilyi will confirm support and
              funding arrangements before services begin.
            </span>
          </label>
          <PolicyLinks />
          <CrisisNote />
        </div>
      )}

      <StepNav onBack={step > 1 ? () => setStep(step - 1) : null} onNext={next} nextLabel={step === 7 ? "Submit NDIS Support Request" : "Continue"} busy={busy} isLast={step === 7} />
    </div>
  );
};

const NdisSupports = () => (
  <div data-testid="ndis-supports-page">
    <PageHero
      kicker="NDIS Supports"
      title={"Story. Skills. Connection.\nParticipation."}
      lede="Culturally responsive, relational and strengths-based support for people with an active NDIS plan — building skills, confidence, independence, relationships, community connection and participation in the things that matter to you. Our approach starts with the person: their goals, strengths, knowledge, relationships, culture and hopes."
    >
      <p className="mt-6 text-xs uppercase tracking-[0.25em] font-bold text-earth/70">Mentoring | Narrative Practice | Skill Development | Community Connection | Movement</p>
      <div className="mt-8">
        <a data-testid="ndis-hero-request-btn" href="#request-form" className="rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105">
          Request NDIS Support
        </a>
      </div>
    </PageHero>

    {/* PATHWAY SELECTOR */}
    <section className="px-6 py-14 bg-cream">
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <Kicker>Who is this pathway for?</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal mb-4">
            Do you currently have an active NDIS plan?
          </h2>
          <div className="mb-10"><Tag>For people with an active NDIS plan</Tag></div>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          <Reveal delay={0.05}>
            <a href="#request-form" data-testid="ndis-path-yes" className="group block rounded-[36px] bg-terracotta text-cream p-9 h-full transition-transform duration-500 hover:-translate-y-2">
              <p className="font-display text-3xl font-light">Yes — I have an active NDIS plan</p>
              <p className="mt-3 text-cream/80 text-sm leading-relaxed">Continue to Ngawilyi's NDIS Support Request.</p>
              <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest">Request NDIS Support <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-[36px] bg-creamdark border border-ochre/25 p-9 h-full" data-testid="ndis-path-no">
              <p className="font-display text-3xl font-light text-charcoal">No — I don't have an NDIS plan</p>
              <p className="mt-3 text-earth/85 text-sm leading-relaxed">
                This particular pathway is for active NDIS participants. You can still access Ngawilyi — no plan
                or external referral required.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link data-testid="ndis-path-private-btn" to="/private-supports" className="rounded-full bg-terracotta px-6 py-3 text-xs font-bold uppercase tracking-widest text-cream">Explore Private Supports</Link>
                <Link data-testid="ndis-path-service-btn" to="/referrals" className="rounded-full border-2 border-charcoal px-6 py-3 text-xs font-bold uppercase tracking-widest text-charcoal">Service & Program Supports</Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* SUPPORTS AVAILABLE */}
    <section className="px-6 py-14 sm:py-20 bg-cream">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-12">
          <Kicker>NDIS supports available</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            Building capacity <span className="italic text-terracotta">doesn't happen across a desk.</span>
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Compass, t: "Mentoring & Capacity Building", d: "Working alongside participants through everyday experiences to develop skills, confidence and independence.", chips: ["Confidence", "Independence", "Communication", "Relationships", "Social skills", "Everyday skills", "Routines", "Education & life transitions", "Identity & belonging", "Decision making", "Problem solving", "Community participation", "Individual NDIS goals"], dark: true },
            { icon: Wrench, t: "Skills Development", d: "Building skills for everyday life — practical abilities that support greater independence and participation.", chips: ["Social & relationship skills", "Communication", "Planning & organisation", "Building routines", "Navigating community", "Everyday tasks", "Decision making", "Problem solving", "Independence", "Life transitions", "Recognising strengths"] },
            { icon: Globe, t: "Community-Based Support", d: "Connection through doing — working towards goals in community environments rather than only in an office.", chips: ["Confidence in community spaces", "Social & recreational participation", "Practising communication", "Independence", "Community connection", "New environments", "Hobbies & interests", "Cultural/community connection", "Real-world practice"] },
            { icon: BookOpen, t: "Narrative Practice", d: "Your story is bigger than the problem. We look to skills, knowledge, relationships, culture, identity, values and hopes — never deficit-based descriptions.", chips: ["Identity", "Confidence", "Preferred ways of being", "Relationships", "Culture & belonging", "Strengths & abilities", "Hopes & future goals"] },
            { icon: Footprints, t: "Movement Within Support", d: "Some people connect better while doing something. Where it fits your NDIS goals, movement and recreation can be part of mentoring and skill development.", chips: ["Walking while yarning", "Activity routines", "Recreational confidence", "Movement-based mentoring", "Motivation", "Social skills through activity"] },
            { icon: Users, t: "Parent & Carer Capacity", d: "Where it fits the participant's goals and funding, we work alongside the important people around them.", chips: ["Preferred strategies", "Relational approaches", "Communication", "Consistency across environments", "Sharing strategies", "Recognising progress"] },
          ].map((s, i) => (
            <Reveal key={s.t} delay={i * 0.06}>
              <div data-testid={`ndis-support-${i}`} className={`rounded-[36px] p-8 h-full transition-transform duration-500 hover:-translate-y-2 ${s.dark ? "bg-terracotta text-cream" : "bg-creamdark border border-ochre/25"}`}>
                <s.icon className={`h-7 w-7 ${s.dark ? "text-cream" : "text-terracotta"}`} strokeWidth={1.5} />
                <h3 className={`mt-4 font-display text-2xl ${s.dark ? "" : "text-charcoal"}`}>{s.t}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${s.dark ? "text-cream/80" : "text-earth/85"}`}>{s.d}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {s.chips.map((c) => (
                    <span key={c} className={`rounded-full px-3 py-1.5 text-[11px] font-semibold ${s.dark ? "bg-cream/12 border border-cream/25 text-cream/90" : "bg-cream border border-ochre/40 text-earth"}`}>{c}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10 rounded-[36px] bg-blush/40 border border-blush p-9 sm:p-12">
            <p className="font-display font-light text-3xl text-charcoal">The activity isn't the goal — <span className="italic text-terracotta">what becomes possible through it is.</span></p>
            <p className="mt-4 text-earth/85 leading-relaxed max-w-3xl">
              The focus always remains on your individual NDIS goals and disability-related support needs.
              Narrative-informed conversations may form part of eligible mentoring, capacity-building and relational
              supports. Where Narrative Therapy is requested specifically as an NDIS-funded therapy support, we
              confirm that your funding, the requested support and applicable provider requirements are suitable
              before the service is confirmed. Movement activities always remain connected to eligible
              disability-related support — Ngawilyi does not provide exercise physiology, physiotherapy, clinical
              rehabilitation or NDIS personal training.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* NO SURPRISES */}
    <section className="px-6 py-14 bg-cream">
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <div className="rounded-[40px] bg-charcoal text-cream p-10 sm:p-14 grain relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-terracotta/25 organic-blob blur-2xl" />
            <div className="relative">
              <Kicker light>No surprises</Kicker>
              <h2 className="font-display font-light text-4xl leading-[1.1]">Know what you're agreeing to before we start.</h2>
              <p className="mt-5 text-cream/80">Before NDIS support begins, Ngawilyi will confirm:</p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {["The agreed support", "Participant goals", "How support will be delivered", "Fees", "Funding/claiming arrangements", "Practitioner", "Service agreement requirements", "Consent requirements", "Cancellation arrangements"].map((t) => (
                  <span key={t} className="rounded-full bg-cream/10 border border-cream/20 px-4 py-2 text-[13px] font-semibold text-cream/85">{t}</span>
                ))}
              </div>
              <p className="mt-6 text-sm text-cream/70">Submitting a request does not automatically create a service agreement.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* REQUEST FORM */}
    <section id="request-form" className="px-6 py-20 sm:py-28 bg-cream">
      <div className="max-w-4xl mx-auto">
        <Reveal className="mb-10">
          <Kicker>Request NDIS Support</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            Tell us what you'd like to make more possible.
          </h2>
          <p className="mt-5 text-earth/85 leading-relaxed max-w-2xl">
            A request, not a commitment — we'll yarn about goals, funding, plan management and practitioner
            matching before anything begins.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <NdisRequestForm />
        </Reveal>
      </div>
    </section>

    <Practitioners kicker="Meet Our Practitioners" title="The people beside you." requestLink="#request-form" />

    <ClosingMessage />
  </div>
);

export default NdisSupports;
